import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronDown, ChevronUp, Download, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["Pending", "Under Review", "Quoted", "Closed"];

const STATUS_STYLES = {
  Pending: "bg-amber-50 text-amber-800 border-amber-200",
  "Under Review": "bg-blue-50 text-blue-800 border-blue-200",
  Quoted: "bg-green-50 text-green-800 border-green-200",
  Closed: "bg-slate-100 text-slate-600 border-slate-300",
};

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(res.data.enquiries);
    } catch {
      toast.error("Could not load enquiries.");
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        await load();
      } catch {
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Status updated to ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <section className="py-24 text-center text-sm text-slate-500" data-testid="admin-loading">
        Loading admin portal...
      </section>
    );
  }

  const pending = enquiries.filter((e) => e.status === "Pending").length;

  return (
    <section data-testid="admin-dashboard" className="bg-[#F8FAFC] min-h-[60vh]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Enquiry Management</h1>
            <p className="mt-1 text-sm text-slate-500">
              Signed in as {user?.email} · {enquiries.length} enquiries · {pending} pending
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="admin-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-2 border border-slate-300 bg-white text-slate-700 text-sm font-medium px-4 py-2 rounded-sm hover:border-slate-500"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-[#0B1329] text-white text-sm font-medium px-4 py-2 rounded-sm hover:bg-slate-800"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-enquiry-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Ref / Date</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Customer</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Contact</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Requirement</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Qty</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Drawing</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-4 py-3" aria-label="Details" />
              </tr>
            </thead>
            <tbody>
              {enquiries.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500" data-testid="admin-empty-state">
                    No enquiries yet. New submissions from the website will appear here.
                  </td>
                </tr>
              )}
              {enquiries.map((e) => (
                <>
                  <tr key={e.id} className="border-b border-slate-100 align-top" data-testid={`admin-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-slate-800">{e.id}</span>
                      <br />
                      <span className="text-xs text-slate-500">
                        {new Date(e.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900">{e.name}</span>
                      <br />
                      <span className="text-xs text-slate-500">{e.company}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {e.email}
                      <br />
                      {e.phone}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-800">{e.requirement}</span>
                      {e.product && (
                        <>
                          <br />
                          <span className="text-xs text-slate-500">{e.product}{e.material ? ` · ${e.material}` : ""}</span>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{e.quantity || "—"}</td>
                    <td className="px-4 py-3">
                      {e.file ? (
                        <a
                          href={`${API}/files/${e.file.stored}`}
                          data-testid={`admin-file-download-${e.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2563EB] hover:text-[#1D4ED8]"
                        >
                          <Download size={13} /> {e.file.original}
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={e.status}
                        data-testid={`admin-status-select-${e.id}`}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        className={`text-xs font-semibold border rounded-sm px-2 py-1.5 ${STATUS_STYLES[e.status]}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        data-testid={`admin-expand-${e.id}`}
                        onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                        className="text-slate-400 hover:text-slate-700"
                        aria-label="Toggle message"
                      >
                        {expanded === e.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </td>
                  </tr>
                  {expanded === e.id && (
                    <tr key={`${e.id}-detail`} className="border-b border-slate-100 bg-slate-50">
                      <td colSpan={8} className="px-4 py-3 text-sm text-slate-600" data-testid={`admin-message-${e.id}`}>
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Message / Technical Details: </span>
                        {e.message || "No additional details provided."}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default AdminDashboard;
